import { useState, useEffect } from "react";
import { Phone } from 'lucide-react';
import { FaWhatsapp, FaWaze, FaEnvelope } from 'react-icons/fa';

const topicImages = {
  'חדלות פרעון והוצאה לפועל': '/חןחדלותפירעון.jpg',
  'נדלן': '/אראלנדלן.jpg',
  'נזיקין ואזרחי': '/מתוקןנזיקין.jpg',
  // תמונת ברירת מחדל
  'default': '/אראלחןיושביםראשי.jpg'
};

const getImageForTopic = (topicName) => {
  const image = topicImages[topicName] || topicImages.default;
  
  // מתחיל טעינה מוקדמת של התמונה
  if (typeof window !== 'undefined') {
    const img = new Image();
    img.src = image;
  }
  
  return image;
};

// OptimizedImage component with screen size adaptations
const OptimizedImage = ({ src, alt, className, style }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [screenType, setScreenType] = useState('standard');
  const placeholderColor = "#97a9b6";

  useEffect(() => {
    const img = new Image();
    img.src = src;
    
    // בדיקה אם התמונה כבר במטמון
    if (img.complete) {
      setImageLoaded(true);
    } else {
      img.onload = () => setImageLoaded(true);
    }

    // זיהוי גודל מסך
    const checkScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1920) {
        setScreenType('large');
      } else if (width >= 1440) {
        setScreenType('medium');
      } else {
        setScreenType('standard');
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      img.onload = null;
      window.removeEventListener('resize', checkScreenSize);
    };
  }, [src]);

  // קביעת מיקום התמונה לפי גודל המסך
  const getImageStyle = () => {
    const baseStyle = { ...style };
    
    // כל המסכים מקבלים object-fit: cover
    baseStyle.objectFit = 'cover';
    
    // התאמת מיקום לפי גודל מסך
    switch (screenType) {
      case 'large': // מסכים גדולים (27 אינץ' ומעלה)
        baseStyle.objectPosition = 'center 25%';
        break;
      case 'medium': // מסכים בינוניים
        baseStyle.objectPosition = 'center 30%';
        break;
      default: // מסכים רגילים
        baseStyle.objectPosition = style?.objectPosition || 'center center';
    }
    
    return baseStyle;
  };

  return (
    <div className="relative w-full h-full overflow-hidden">
      <div
        className={`absolute inset-0 ${imageLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        style={{ backgroundColor: placeholderColor }}
      />
      <img
        src={src}
        alt={alt}
        loading="eager"
        fetchPriority="high"
        className={`${className} ${imageLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
        style={getImageStyle()}
      />
    </div>
  );
};

const HeaderLayout = ({ topicData }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);

  useEffect(() => {
    // טעינת התמונה והגדרת גודל המסך
    const loadImageAndSetup = async () => {
      const imagePath = getImageForTopic(topicData?.name);
      setBackgroundImage(imagePath);

      // הוספת preload רק לתמונה הספציפית
      const preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.as = 'image';
      preloadLink.href = imagePath;
      document.head.appendChild(preloadLink);

      // בדיקת גודל מסך
      const checkScreenSize = () => {
        const width = window.innerWidth;
        setIsMobile(width < 768);
        setIsLargeScreen(width >= 1920);
      };
      
      checkScreenSize();
      window.addEventListener("resize", checkScreenSize);

      return () => {
        window.removeEventListener("resize", checkScreenSize);
        preloadLink.remove();
      };
    };

    loadImageAndSetup();
  }, [topicData?.name]);

  if (!backgroundImage) return null; // מצב טעינה

  if (isMobile) {
    return (
      <div className="relative min-h-[600px] lg:min-h-[800px] overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <OptimizedImage 
            src={backgroundImage}
            alt={topicData?.name || 'תמונת רקע'}
            className="w-full h-full object-cover animate-ken-burns"
            style={{ objectPosition: 'center 30%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#97a9b6]/80 to-[#35434e]/80" />
        </div>

        {/* Content */}
        <div className="relative max-w-6xl mx-auto px-4 h-full py-20">
          <div className="flex flex-col justify-center h-full">
            <div className="text-white space-y-8 max-w-3xl text-center md:text-right">
              <h1 className="text-4xl lg:text-5xl font-bold mb-8 animate-fade-in">
                משרד עורכי דין -מיכלסון דותן ושות
              </h1>
              <p className="text-xl lg:text-2xl mb-12 animate-slide-up">
                מתמחים ב{topicData.name}
              </p>
              
              <div className="flex flex-wrap justify-center md:justify-start gap-6 animate-fade-in-up">
                <a 
                  href="tel:0532788686" 
                  className="bg-white/20 backdrop-blur-md text-white px-6 lg:px-8 py-4 rounded-xl font-medium 
                           hover:bg-white/30 transition duration-300 flex flex-col items-center
                           transform hover:scale-105"
                >
                  <Phone className="w-6 lg:w-8 h-6 lg:h-8 text-white group-hover:scale-110 transition-transform duration-300" />
                </a>
                <a 
                  href="https://wa.me/972532788686" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white/20 backdrop-blur-md text-white px-6 lg:px-8 py-4 rounded-xl font-medium 
                           hover:bg-white/30 transition duration-300 flex flex-col items-center
                           transform hover:scale-105"
                >
                  <FaWhatsapp className="w-6 lg:w-8 h-6 lg:h-8 text-white group-hover:scale-110 transition-transform duration-300" />
                </a>
                
                <a 
                  href="mailto:office@mdc-lawfirm.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white/20 backdrop-blur-md text-white px-6 lg:px-8 py-4 rounded-xl font-medium 
                           hover:bg-white/30 transition duration-300 flex flex-col items-center
                           transform hover:scale-105"
                >
                  <FaEnvelope className="w-6 lg:w-8 h-6 lg:h-8 text-white group-hover:scale-110 transition-transform duration-300" />
                </a>
                
                <a 
                  href="https://waze.com/ul?ll=31.9850714,34.7701406&navigate=yes" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white/20 backdrop-blur-md text-white px-6 lg:px-8 py-4 rounded-xl font-medium 
                           hover:bg-white/30 transition duration-300 flex flex-col items-center
                           transform hover:scale-105"
                >
                  <FaWaze className="w-6 lg:w-8 h-6 lg:h-8 text-white group-hover:scale-110 transition-transform duration-300" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // אפשרות 8: עיצוב תלת-ממדי מלא ללא רווחים
  if (isLargeScreen) {
    return (
      <div className="min-h-screen relative overflow-hidden bg-gradient-to-r from-[#97a9b6]/80 to-[#35434e]/80">
        {/* שכבה תלת-ממדית אחורית */}
        <div className="absolute -top-16 -right-16 w-2/3 h-[600px] bg-white/5 rounded-xl transform rotate-6 shadow-xl"></div>
  
        {/* תיבה מרכזית */}
        <div className="relative flex h-screen">
          {/* תוכן בצד ימין */}
          <div className="w-1/2 py-16 px-12 md:px-24 flex items-center bg-gradient-to-r from-[#97a9b6] to-[#35434e] z-10">
            <ContentSection topicData={topicData} />
          </div>
  
          {/* תמונה בצד שמאל */}
          <div className="w-1/2 relative">
            <OptimizedImage
              src={backgroundImage}
              alt={topicData?.name || 'תמונת רקע'}
              className="w-full h-full object-cover"
            />
            {/* שכבת הבלטה לתמונה */}
            <div className="absolute inset-0 shadow-inner pointer-events-none border-l border-white/10"></div>
          </div>
        </div>
  
      
      </div>
    );
  }
  

  // למסכים רגילים, נשאר עם העיצוב הקיים
  return (
    <div className="min-h-[800px] flex">
      {/* Content Section - Right Side */}
      <div className="w-1/2 p-12 flex items-center bg-gradient-to-r from-[#97a9b6] to-[#35434e]">
        <ContentSection topicData={topicData} />
      </div>

      {/* Image Section - Left Side */}
      <div className="w-1/2 relative overflow-hidden">
        <div className="absolute inset-0">
          <OptimizedImage
            src={backgroundImage}
            alt={topicData?.name || 'תמונת רקע'}
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </div>
  );
};

// ContentSection component
const ContentSection = ({ topicData }) => (
  <div className="text-white space-y-8">
    <h1 className="text-4xl lg:text-5xl font-bold mb-8 animate-fade-in">
      משרד עורכי דין - מיכלסון דותן ושות
    </h1>
    <p className="text-xl lg:text-2xl mb-12 animate-slide-up">
      מתמחים ב{topicData.name}
    </p>
    
    <div className="flex flex-wrap gap-6 animate-fade-in-up">
      <a 
        href="tel:0532788686" 
        className="bg-white/20 backdrop-blur-md text-white px-6 lg:px-8 py-4 rounded-xl font-medium 
                 hover:bg-white/30 transition duration-300 flex items-center
                 transform hover:scale-105"
      >
        <Phone className="w-6 h-6" />
      </a>
      
      <a 
        href="https://wa.me/972532788686" 
        target="_blank" 
        rel="noopener noreferrer"
        className="bg-white/20 backdrop-blur-md text-white px-6 lg:px-8 py-4 rounded-xl font-medium 
                 hover:bg-white/30 transition duration-300 flex items-center
                 transform hover:scale-105"
      >
        <FaWhatsapp className="w-6 h-6" />
      </a>
      
      <a 
        href="mailto:office@mdc-lawfirm.com" 
        target="_blank" 
        rel="noopener noreferrer"
        className="bg-white/20 backdrop-blur-md text-white px-6 lg:px-8 py-4 rounded-xl font-medium 
                 hover:bg-white/30 transition duration-300 flex items-center
                 transform hover:scale-105"
      >
        <FaEnvelope className="w-6 h-6" />
      </a>
      
      <a 
        href="https://waze.com/ul?ll=31.9850714,34.7701406&navigate=yes" 
        target="_blank" 
        rel="noopener noreferrer"
        className="bg-white/20 backdrop-blur-md text-white px-6 lg:px-8 py-4 rounded-xl font-medium 
                 hover:bg-white/30 transition duration-300 flex items-center
                 transform hover:scale-105"
      >
        <FaWaze className="w-6 h-6" />
      </a>
    </div>
  </div>
);

export default HeaderLayout;